import seon from '@seontechnologies/seon-javascript-sdk';
import { captureError } from '@smg-real-estate/browser-rum';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import { patchSeonSession } from '@/api/insertions';
import { getInitialSeonState } from '@/stores/types/seon';
import { SourcePlatform } from '@/types';

export const useSeonStore = defineStore('seon', () => {
  const seonSession = ref(getInitialSeonState().seonSession);

  const configure = async (payload: { insertionId: string; userId: string }): Promise<void> => {
    const { insertionId, userId } = payload;
    if (!insertionId || !userId) {
      captureError(new Error('Missing required parameter: insertionId or userId'), {
        service: 'seon',
        action: 'configure',
      });
      return;
    }

    try {
      seon.init();
      const config = {
        geolocation: {
          canPrompt: false,
        },
        networkTimeoutMs: 2000,
        fieldTimeoutMs: 2000,
        region: 'eu',
        silentMode: true,
      };

      seonSession.value = await seon.getSession(config);
      console.log('SEON Session initialized successfully');
    } catch (e) {
      captureError(e, { service: 'seon', action: 'configure' });
      if (e.name === 'SEONConfigError') {
        console.error('SEON Config Error:', e.message);
      } else {
        console.error('SEON Unknown Error:', e);
      }
    }
  };

  const saveSeonSession = async ({
    ownerId,
    insertionId,
    userEnrolledInMFA,
    requiredMFA,
    mfaEnrollmentDateUtc,
  }: {
    ownerId: string;
    insertionId: string;
    userEnrolledInMFA: boolean;
    requiredMFA: boolean;
    mfaEnrollmentDateUtc?: string;
  }): Promise<void> => {
    try {
      if (seonSession.value) {
        await patchSeonSession(ownerId, insertionId, SourcePlatform.SMG, seonSession.value, {
          userEnrolledInMFA,
          requiredMFA,
          mfaEnrollmentDateUtc,
        });
      }
    } catch (error) {
      captureError(error, { service: 'seon', action: 'save', insertionId });
    }
  };

  const clear = (): void => {
    seonSession.value = null;
  };

  return {
    seonSession,
    configure,
    clear,
    saveSeonSession,
  };
});
